import { useEffect, useMemo, useState } from "react";

import { ensureCsrfToken } from "../../api";

import Swal from "sweetalert2";

const MATCHING_OPTIONS = ["Invoice Number", "PO Number", "GRN Number"];
const NARRATION_FIELDS = [
  { value: "invoice_no", label: "Invoice No" },
  { value: "vendor_name", label: "Vendor Name" },
  { value: "amount", label: "Amount" },
  { value: "date", label: "Date" },
  { value: "po_no", label: "PO No" },
];

const BLOCKING_ROWS = [
  { key: "pan_inactive", label: "Vendor PAN Inactive" },
  { key: "aadhar_pan", label: "Vendor Aadhar & PAN linked (Individual & Proprietor)" },
  { key: "gst_inactive", label: "GST Inactive" },
  { key: "gstr1", label: "GSTR1 not Filed", hasMonths: true },
  { key: "gstr3b", label: "GSTR3B Filed (not filed flag)", hasMonths: true },
  { key: "gst_2b", label: "GST not appearing in 2B (13 days rule)", note: "Under Development" },
  { key: "new_vendor", label: "New Vendor / Irregular Transactions" },
  { key: "regular_vendor", label: "Regular Vendor" },
  { key: "prev_gst", label: "Previous invoice GST not appearing" },
  
];


const DEFAULT_OPEN_SECTIONS = {
  posting: true,
  
  reporting: false,
  
  narration: false,
  
};

function getCurrentFY() {
  const today = new Date();
  return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
}

function getFYOptions() {
  const currentFY = getCurrentFY();
  return Array.from({ length: 10 }, (_, index) => {
    const fy = currentFY - index;
    return {
      value: String(fy),
      label: `FY ${fy}-${String(fy + 1).slice(-2)}`,
    };
  });
}

function getMonthOptions(selectedFY) {
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentFY = getCurrentFY();

  const months = [
    ["April", "3"],
    ["May", "4"],
    ["June", "5"],
    ["July", "6"],
    ["August", "7"],
    ["September", "8"],
    ["October", "9"],
    ["November", "10"],
    ["December", "11"],
    ["January", "0"],
    ["February", "1"],
    ["March", "2"],
  ];

  return months.filter(([, index]) => {
    if (String(selectedFY) !== String(currentFY)) {
      return true;
    }

    const monthIndex = Number(index);
    return currentMonth >= 3
      ? monthIndex <= currentMonth
      : monthIndex <= currentMonth || monthIndex >= 3;
  });
}

function getCurrentFYMonthOptions() {
  const today = new Date();
  const currentMonth = today.getMonth();
  const months = [
    { name: "April", index: "3" },
    { name: "May", index: "4" },
    { name: "June", index: "5" },
    { name: "July", index: "6" },
    { name: "August", index: "7" },
    { name: "September", index: "8" },
    { name: "October", index: "9" },
    { name: "November", index: "10" },
    { name: "December", index: "11" },
    { name: "January", index: "0" },
    { name: "February", index: "1" },
    { name: "March", index: "2" },
  ];

  return months.filter((month) => {
    const monthIndex = Number(month.index);
    if (currentMonth >= 3) {
      return monthIndex >= 3 && monthIndex <= currentMonth;
    }
    return monthIndex >= 3 || monthIndex <= currentMonth;
  });
}

function createDefaultConfig() {
  const currentFY = String(getCurrentFY());
  const currentMonths = getCurrentFYMonthOptions();
  const previousMonths = getMonthOptions(currentFY);
  
  const blocking = BLOCKING_ROWS.reduce((accumulator, row) => {
    accumulator[row.key] = {
      acc: row.key === "gst_inactive" ? "Y" : "N",
      pay: row.key === "regular_vendor" ? "N" : "Y",
      months: "",
      code: "",
    };
    return accumulator;
  }, {});

  return {
    monthly_close: {
      monthly_close: "N",
      date_of_entry: "system",
      auto_on_date: "5",
      auto_or_manual: "automatic",
      manual_close: {
        PreviousYr: currentFY,
        PreviousYrMonth: previousMonths[0]?.[1] || "3",
        CurrentYrMonth: currentMonths[0]?.index || "3",
        scenario_same_month: "system",
        scenario_books_closed: "first_day",
        scenario_books_not_closed: "last_date",
      },
      auto_close: {
        scenario_same_month: "system",
        scenario_books_closed: "first_day",
        scenario_books_not_closed: "last_date",
      },
    },
    
    
    currency: {
      reporting_currency_name: "",
      reporting_currency_code: "",
      exchange_rate_tolerance: "",
    },
    narration: {
      fields: ["invoice_no","vendor_name","date","po_no"],
      symbol: "!",
    },
    
    
  };
}

function mergeLoadedConfig(payload) {
  const next = createDefaultConfig();
  if (!payload) {
    return next;
  }

  if (payload.monthly_close) {
    next.monthly_close = {
      ...next.monthly_close,
      ...payload.monthly_close,
      manual_close: {
        ...next.monthly_close.manual_close,
        ...(payload.monthly_close.manual_close || {}),
      },
      auto_close: {
        ...next.monthly_close.auto_close,
        ...(payload.monthly_close.auto_close || {}),
      },
    };
  }

  
  if (payload.currency) {
    next.currency = { ...next.currency, ...payload.currency };
  }

  if (payload.narration) {
    next.narration = {
      ...next.narration,
      ...payload.narration,
      fields: Array.isArray(payload.narration.fields) ? payload.narration.fields : [],
    };
  }


  return next;
}

async function parseJsonResponse(response) {
  const contentType = (response.headers.get("content-type") || "").toLowerCase();
  if (contentType.includes("application/json")) {
    return {
      isJson: true,
      payload: await response.json(),
      text: "",
    };
  }
  return {
    isJson: false,
    payload: null,
    text: await response.text(),
  };
}

function normalizeNonJsonError(response, text, fallback) {
  const body = String(text || "");
  const lower = body.toLowerCase();
  const looksLikeHtml = lower.includes("<html") || lower.includes("<!doctype html");
  const looksLikeLogin = lower.includes("miro app login") || lower.includes("name=\"password\"");

  if (response.redirected || response.url.includes("next=") || looksLikeLogin) {
    return "Session is not authenticated. Please login again.";
  }
  if (looksLikeHtml) {
    return fallback;
  }
  return body || fallback;
}

function Section({ title, subtitle, open, onToggle, children }) {
  return (
    <article className="config-card">
      <button className="config-card__head" type="button" onClick={onToggle}>
        <div>
          <div className="config-card__title">{title}</div>
          <div className="config-card__subtitle">{subtitle}</div>
        </div>
        <span className="config-card__toggle">{open ? "Hide" : "Show"}</span>
      </button>
      {open ? <div className="config-card__body">{children}</div> : null}
    </article>
  );
}

function RadioGroup({ name, value, options, onChange }) {
  return (
    <div className="config-inline">
      {options.map((option) => (
        <label key={option.value} className="config-choice">
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <span>{option.label}</span>
        </label>
      ))}
    </div>
  );
}

export default function NonPoConfigurationView() {
  const [config, setConfig] = useState(createDefaultConfig);
  const [openSections, setOpenSections] = useState(DEFAULT_OPEN_SECTIONS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const fyOptions = useMemo(() => getFYOptions(), []);
  const previousYearMonthOptions = useMemo(
    () => getMonthOptions(config.monthly_close.manual_close.PreviousYr),
    [config.monthly_close.manual_close.PreviousYr],
  );
  const currentFyMonthOptions = useMemo(() => getCurrentFYMonthOptions(), []);

  

  

  useEffect(() => {
    let cancelled = false;

    async function loadConfiguration() {
      setLoading(true);
      setError("");

      try {
        const response = await fetch("/api/configuration/", {
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        });
        const { isJson, payload, text } = await parseJsonResponse(response);
        if (!isJson) {
          throw new Error(
            normalizeNonJsonError(
              response,
              text,
              "Unable to load saved configuration.",
            ),
          );
        }

        if (!cancelled) {
          if (payload.status === "success") {            
            setConfig(mergeLoadedConfig(payload.data));
          }
          setStatus("");
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError.message || "Unable to load saved configuration.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadConfiguration();

    return () => {
      cancelled = true;
    };
  }, []);

  function toggleSection(sectionKey) {
    setOpenSections((current) => ({
      ...current,
      [sectionKey]: !current[sectionKey],
    }));
  }

  function updateTopLevel(key, value) {
    setConfig((current) => ({
      ...current,
      [key]: value,
    }));
  }

  function updateSection(sectionKey, field, value) {
    setConfig((current) => ({
      ...current,
      [sectionKey]: {
        ...current[sectionKey],
        [field]: value,
      },
    }));
  }

  function updateNested(sectionKey, nestedKey, field, value) {
    setConfig((current) => ({
      ...current,
      [sectionKey]: {
        ...current[sectionKey],
        [nestedKey]: {
          ...current[sectionKey][nestedKey],
          [field]: value,
        },
      },
    }));
  }

  function updateBlocking(rowKey, field, value) {
    setConfig((current) => ({
      ...current,
      blocking: {
        ...current.blocking,
        [rowKey]: {
          ...current.blocking[rowKey],
          [field]: value,
        },
      },
    }));
  }

  function handleNarrationFieldToggle(value) {
    setConfig((current) => {
      const alreadySelected = current.narration.fields.includes(value);
      return {
        ...current,
        narration: {
          ...current.narration,
          fields: alreadySelected
            ? current.narration.fields.filter((item) => item !== value)
            : [...current.narration.fields, value],
        },
      };
    });
  }

  function buildPayload() {
    return {
      monthly_close: config.monthly_close,
      currency: config.currency,
      narration: config.narration,
      
    };
  }

  async function handleSave() {
    setSaving(true);
    
    try {
      
      
      const csrfToken = await ensureCsrfToken();
      const formData = new FormData();
      formData.append("payload", JSON.stringify(buildPayload()));

      const response = await fetch("/api/configuration-nonpo/save/", {
        method: "POST",
        headers: {
          "X-CSRFToken": csrfToken,
          Accept: "application/json",
        },
        credentials: "include",
        body: formData,
      });

      const { isJson, payload, text } = await parseJsonResponse(response);
      if (!isJson) {
        throw new Error(
          normalizeNonJsonError(
            response,
            text,
            "Unable to save configuration.",
          ),
        );
      }

      if (!response.ok || payload.status !== "success") {
        throw new Error(payload.message || "Unable to save configuration.");
      }
      Swal.fire({
        icon: "success",
        title: "Success",
        text: payload.message || "Configuration saved successfully.",
        confirmButtonText: "OK",
      });
      
    } catch (requestError) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: requestError.message || "Unable to save configuration.",
        confirmButtonText: "OK",
      });
      
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="workspace-panel">Loading configuration…</div>;
  }

  return (
    <section className="config-view">
      <div className="config-view__header workspace-panel">
        <div>
          <p className="eyebrow">Basic Configurations</p>
          <h2>Accounting Configuration</h2>
          <p className="config-view__intro">
            Non-PO Invoice: Configure accounting rules and validations for invoices without a Purchase Order, 
            including verification, approval, and transaction posting.
          </p>
        </div>
        <button className="primary-button" type="button" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Submit Configuration"}
        </button>
      </div>

      {status ? <div className="config-banner config-banner--success">{status}</div> : null}
      {error ? <div className="config-banner config-banner--error">{error}</div> : null}

      <Section
        title="1. Posting Date"
        subtitle="Monthly close and posting date rules"
        open={openSections.posting}
        onToggle={() => toggleSection("posting")}
      >
        <div className="config-grid">
          <div className="config-field">
            <label>Keep provision of monthly close of WFS?</label>
            <RadioGroup
              name="monthly_close"
              value={config.monthly_close.monthly_close}
              options={[
                { value: "N", label: "No" },
                { value: "Y", label: "Yes" },
              ]}
              onChange={(value) => updateSection("monthly_close", "monthly_close", value)}
            />
          </div>

          {config.monthly_close.monthly_close === "N" ? (
            <div className="config-field">
              <label>If No, Date of Entry</label>
              <select
                value={config.monthly_close.date_of_entry}
                onChange={(event) => updateSection("monthly_close", "date_of_entry", event.target.value)}
              >
                <option value="system">System Date</option>
                <option value="invoice">Invoice Date</option>
                <option value="migo">MIGO Date</option>
              </select>
            </div>
          ) : (
            <div className="config-field">
              <label>If Yes, Automatic or Manual?</label>
              <select
                value={config.monthly_close.auto_or_manual}
                onChange={(event) => updateSection("monthly_close", "auto_or_manual", event.target.value)}
              >
                <option value="automatic">Automatic</option>
                <option value="manual">Manual</option>
              </select>
            </div>
          )}
        </div>

        {config.monthly_close.monthly_close === "Y" ? (
          <>
            {config.monthly_close.auto_or_manual === "automatic" ? (
              <div className="config-grid">
                <div className="config-field">
                  <label>Automatic on</label>
                  <input
                    type="number"
                    min="1"
                    max="28"
                    value={config.monthly_close.auto_on_date}
                    onChange={(event) => updateSection("monthly_close", "auto_on_date", event.target.value)}
                    placeholder="day of month"
                  />
                </div>
              </div>
            ) : (
              <div className="config-grid">
                <div className="config-field">
                  <label>Select Previous Year</label>
                  <select
                    value={config.monthly_close.manual_close.PreviousYr}
                    onChange={(event) => {
                      
                      updateNested(
                        "monthly_close",
                        "manual_close",
                        "PreviousYrMonth",
                        event.target.value
                      );
                    }}
                  >
                    {fyOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="config-field">
                  <label>Select Previous Year Month</label>
                  <select
                    value={config.monthly_close.manual_close.PreviousYrMonth ?? ""}
                    onChange={(event) =>
                      updateNested("monthly_close", "manual_close", "PreviousYrMonth", event.target.value)
                    }
                  >
                    {previousYearMonthOptions.map(([name, value]) => (
                      <option key={value} value={value}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="config-field">
                  <label>Select Current Year Month</label>
                  <select
                    value={config.monthly_close.manual_close.CurrentYrMonth ?? ""}
                    onChange={(event) =>
                      updateNested("monthly_close", "manual_close", "CurrentYrMonth", event.target.value)
                    }
                  >
                    {currentFyMonthOptions.map((option) => (
                      <option key={option.index} value={option.index}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="config-grid">
              <div className="config-field">
                <label>IF month(invoice) = month(entry)</label>
                <select
                  value={
                    config.monthly_close.auto_or_manual === "manual"
                      ? config.monthly_close.manual_close.scenario_same_month
                      : config.monthly_close.auto_close.scenario_same_month
                  }
                  onChange={(event) =>
                    updateNested(
                      "monthly_close",
                      config.monthly_close.auto_or_manual === "manual" ? "manual_close" : "auto_close",
                      "scenario_same_month",
                      event.target.value,
                    )
                  }
                >
                  <option value="system">System Date</option>
                  <option value="invoice">Invoice Date</option>
                  <option value="migo">MIGO Date</option>
                </select>
              </div>

              <div className="config-field">
                <label>IF month(invoice) != month(entry), books closed</label>
                <select
                  value={
                    config.monthly_close.auto_or_manual === "manual"
                      ? config.monthly_close.manual_close.scenario_books_closed
                      : config.monthly_close.auto_close.scenario_books_closed
                  }
                  onChange={(event) =>
                    updateNested(
                      "monthly_close",
                      config.monthly_close.auto_or_manual === "manual" ? "manual_close" : "auto_close",
                      "scenario_books_closed",
                      event.target.value,
                    )
                  }
                >
                  <option value="system">System Date</option>
                  <option value="first_day">First Day of Month</option>
                  <option value="block_entry">Block The Entry</option>
                </select>
              </div>

              <div className="config-field">
                <label>IF month(invoice) != month(entry), books not closed</label>
                <select
                  value={
                    config.monthly_close.auto_or_manual === "manual"
                      ? config.monthly_close.manual_close.scenario_books_not_closed
                      : config.monthly_close.auto_close.scenario_books_not_closed
                  }
                  onChange={(event) =>
                    updateNested(
                      "monthly_close",
                      config.monthly_close.auto_or_manual === "manual" ? "manual_close" : "auto_close",
                      "scenario_books_not_closed",
                      event.target.value,
                    )
                  }
                >
                  <option value="last_date">Last date of invoice month</option>
                  <option value="system">System Date</option>
                  <option value="invoice">Invoice Date</option>
                  <option value="migo">MIGO Date</option>
                </select>
              </div>
            </div>
          </>
        ) : null}
      </Section>

      

      <Section
        title="5. Reporting Currency"
        subtitle="Currently under development for import invoices"
        open={openSections.reporting}
        onToggle={() => toggleSection("reporting")}
      >
        <p className="config-note">
          Reporting currency handling is still under development in the original Django template, so
          this React version keeps the same placeholder behavior.
        </p>
      </Section>

      

      <Section
        title="7. Narration String"
        subtitle="Narration composition"
        open={openSections.narration}
        onToggle={() => toggleSection("narration")}
      >
        <div className="config-field">
          <label>Narration fields</label>
          <div className="config-check-grid">
            {NARRATION_FIELDS.map((field) => (
              <label key={field.value} className="config-check">
                <input
                  type="checkbox"
                  checked={config.narration.fields.includes(field.value)}
                  onChange={() => handleNarrationFieldToggle(field.value)}
                />
                <span>{field.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="config-field">
          <label>Delimiter / symbol</label>
          <select
            value={config.narration.symbol}
            onChange={(event) => updateSection("narration", "symbol", event.target.value)}
          >
            {["!", "@", "#", "$", "%", "^", "&", "*"].map((symbol) => (
              <option key={symbol} value={symbol}>
                {symbol}
              </option>
            ))}
          </select>
        </div>
      </Section>

      
    </section>
  );
}
